import Head from "next/head";
import { ViewModel } from "../../lib/viewModel/createViewModel";
import { ArticlePageStory } from "../../lib/storyblok/types/stories/ArticlePageStory";
import { StoryblokStoryComponent } from "../../lib/storyblok/types/StoryblokComponent";
import { DynamicBlocks } from "../DynamicBlock";
import { useTranslation } from "next-i18next";
import { useRelations } from "../../lib/storyblok/useRelations";
import { useLinks } from "../../lib/storyblok/useLinks";
import Image from "next/image";

import ArticleHeadingUI from "../../ui/ArticleHeading";
import AuthorsUi from "../../ui/Authors";
import StoryblokImage from "../StoryblokImage";
import { Author } from "../../lib/storyblok/types/stories/Author";
import TagsAndShareWrapper from "../../ui/TagsAndShareWrapper";
import Tag from "../Tag";
import WeChat from "../../public/images/wechat.jpg";
import { useViewmodel } from "../../lib/storyblok/ViewmodelContext";
import ShareItems from "../../ui/Share/components/ShareItems";
import EditorialWithoutSpacing from "../Nestable/EditorialWithoutSpacing";
import CTAForm from "../Nestable/CtaForm";
import { FormatDateRange } from "../../lib/utils/FormatDateRange";
import { isValidImageData } from "../../lib/utils/imageUtils";
import { useSeoImage } from "../../hooks/useSeoImage";

type VM = ViewModel<ArticlePageStory>;

const ArticlePage: StoryblokStoryComponent<VM> = ({ story }) => {
  const resolveLink = useLinks();
  const { getRelation } = useRelations();
  const { t } = useTranslation(["articlepage", "common"]);

  const {
    pageTitle,
    author,
    authors,
    collaborators,
    createdAt,
    readTime,
    body,
    ctaForm,
    contentGrid,
    tagsProductCategories,
    tagsSolutions,
    otherTags,
  } = story.content;
  const { globalLinks, locale, site } = useViewmodel();

  const blogBaseUrl = globalLinks.mainBlogFilterPage
    ? resolveLink(globalLinks.mainBlogFilterPage)
    : "";

  const { seoContentImage, seoFallbackImage } = useSeoImage({
    seoImage: story.content.seoImage,
    locale,
    site,
  });

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: pageTitle || "",
    image: seoContentImage || seoFallbackImage,
    datePublished: createdAt || "",
    dateModified: createdAt || "",
    author: [
      {
        "@type": "Person",
        name: author || "",
      },
    ],
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      </Head>
      <ArticleHeadingUI
        title={pageTitle}
        author={author}
        createdAt={FormatDateRange(createdAt)}
        readTime={readTime}
        byLabel={t("by")}
        minutesLabel={t("minutes")}
        parent={blogBaseUrl}
      >
        <ShareItems />
      </ArticleHeadingUI>
      <DynamicBlocks
        blocks={body}
        overrides={{ editorial: EditorialWithoutSpacing }}
      />
      <TagsAndShareWrapper>
        <TagsAndShareWrapper.TagsWrapper>
          {tagsProductCategories?.map((tag) => {
            const url = `${blogBaseUrl}?products=${tag}#filter`;
            return <Tag key={tag} tag={t(tag, { ns: "tags" })} url={url} />;
          })}
          {tagsSolutions?.map((tag) => {
            const url = `${blogBaseUrl}?solutions=${tag}#filter`;
            return <Tag key={tag} tag={t(tag, { ns: "tags" })} url={url} />;
          })}
          {otherTags?.map((tag) => {
            const url = `${blogBaseUrl}?tags=${tag}#filter`;
            return <Tag key={tag} tag={t(tag, { ns: "tags" })} url={url} />;
          })}
        </TagsAndShareWrapper.TagsWrapper>

        <TagsAndShareWrapper.ShareWrapper
          weChat={
            locale === "zh" && (
              <Image src={WeChat} quality={100} sizes="100vw" alt="WeChat" />
            )
          }
        >
          <ShareItems />
        </TagsAndShareWrapper.ShareWrapper>
      </TagsAndShareWrapper>
      {authors?.length > 0 && (
        <AuthorsUi title={t("written-by")}>
          {authors.map((author) => {
            const story = getRelation<Author>(author);

            if (story) {
              const { uuid, content, name } = story;
              return (
                <AuthorsUi.Author
                  key={uuid}
                  image={
                    content.image &&
                    isValidImageData(content.image) && (
                      <StoryblokImage
                        sizes="100vw"
                        resize={{ width: 256 }}
                        image={content.image}
                        layout="fill"
                        alt={name}
                      />
                    )
                  }
                  name={name}
                  rolesAndCompany={content.rolesAndCompany}
                  description={content.description}
                  facebookLink={
                    content.facebookLink?.url &&
                    resolveLink(content.facebookLink)
                  }
                  linkedinLink={
                    content.linkedinLink?.url &&
                    resolveLink(content.linkedinLink)
                  }
                  twitterLink={
                    content.twitterLink?.url && resolveLink(content.twitterLink)
                  }
                  link={content.link?.url && resolveLink(content.link)}
                  showMoreLabel={t("show-more", { ns: "common" })}
                  showLessLabel={t("show-less", { ns: "common" })}
                />
              );
            }
          })}
        </AuthorsUi>
      )}
      {collaborators?.length > 0 && (
        <AuthorsUi title={t("collaboration")}>
          {collaborators.map((collaborator) => {
            const story = getRelation<Author>(collaborator);

            if (story) {
              const { uuid, content } = story;

              return (
                <AuthorsUi.Author
                  key={uuid}
                  image={
                    content.image &&
                    isValidImageData(content.image) && (
                      <StoryblokImage
                        sizes="100vw"
                        resize={{ width: 256 }}
                        image={content.image}
                        layout="fill"
                        alt={content.name}
                      />
                    )
                  }
                  name={content.name}
                  rolesAndCompany={content.rolesAndCompany}
                  description={content.description}
                  facebookLink={
                    content.facebookLink?.url &&
                    resolveLink(content.facebookLink)
                  }
                  linkedinLink={
                    content.linkedinLink?.url &&
                    resolveLink(content.linkedinLink)
                  }
                  twitterLink={
                    content.twitterLink?.url && resolveLink(content.twitterLink)
                  }
                  link={content.link?.url && resolveLink(content.link)}
                  showMoreLabel={t("show-more", { ns: "common" })}
                  showLessLabel={t("show-less", { ns: "common" })}
                />
              );
            }
          })}
        </AuthorsUi>
      )}
      <DynamicBlocks blocks={[...(ctaForm ? ctaForm : []), ...contentGrid]} />
      {locale != "ja" && locale != "zh" && (
        <>
          <div id="subscribe"></div>
          <CTAForm
            title={t("blog-subscribe-title")}
            formUrl={t("blog-subscribe-formUrl")}
            text={t("blog-subscribe-description")}
            _uid={""}
            component={"ctaForm"}
            _editable={""}
            spacing={"both"}
            copy={""}
            image={undefined}
            style={"white"}
            divider={"swooshBlueTop"}
          />
        </>
      )}
    </>
  );
};

export default ArticlePage;
